import {
  DEFAULT_LIST_PAGE,
  DEFAULT_LIST_PAGE_SIZE,
} from '#/utils/listUrl/listPaginationDefaults';
import { MULTI_VALUE_FILTER_SNAKE_KEYS } from '#/modules/commonComponents/table/listFilterKeys';

export { MULTI_VALUE_FILTER_SNAKE_KEYS };
export { DEFAULT_LIST_PAGE_SIZE } from '#/utils/listUrl/listPaginationDefaults';

const RESERVED_KEYS = new Set(['page', 'page_size', 'keyword', 'sort']);


function filterKeyToCamel(key) {
  return key.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
}

function filterKeyToSnake(key) {
  return key.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
}

function parseSortParams(searchParams) {
  const sorts = searchParams.getAll('sort');
  if (sorts.length > 0) {
    return sorts.map((item) => {
      const [id, desc] = item.split(',');
      return { id, desc: desc === 'desc' };
    });
  }
  return [];
}

function parseFilterParams(searchParams) {
  const filters = {};
  const filterKeys = [...new Set(searchParams.keys())].filter((key) => !RESERVED_KEYS.has(key));

  filterKeys.forEach((key) => {
    const values = searchParams.getAll(key).filter(Boolean);
    if (values.length === 0) {
      return;
    }

    const camelKey = filterKeyToCamel(key);
    const useArrayValue = MULTI_VALUE_FILTER_SNAKE_KEYS.has(key) || values.length > 1;

    if (useArrayValue) {
      filters[camelKey] = values;
      return;
    }

    filters[camelKey] = values[0];
  });

  return filters;
}

export function parseListSearchParams(searchParams) {
  const page = Number(searchParams.get('page') || DEFAULT_LIST_PAGE);
  const pageSize = Number(searchParams.get('page_size') || DEFAULT_LIST_PAGE_SIZE);
  const keyword = searchParams.get('keyword') || '';

  return {
    page,
    pageSize,
    keyword,
    sort: parseSortParams(searchParams),
    filters: parseFilterParams(searchParams),
  };
}

/**
 * 多選 filter 改為單一逗號參數（`status=A,B`）。
 * CRM／售後列表契約為逗號 OR；重複已辨識 key 回 400 QUERY_PARAM_INVALID（be doc dd7f0319）。
 */
export function joinMultiValueFilters(listQuery) {
  const filters = Object.fromEntries(
    Object.entries(listQuery?.filters || {}).map(([key, value]) => (
      Array.isArray(value) ? [key, value.filter(Boolean).join(',')] : [key, value]
    )),
  );
  return { ...listQuery, filters };
}

// TODO: 重複 key 多選之篩選語意待後端／資料量驗證；僅 meta.multiValue 欄位才送陣列
export function listQueryToApiParams(listQuery) {
  const params = new URLSearchParams();
  params.set('page', String(listQuery.page || DEFAULT_LIST_PAGE));
  params.set('page_size', String(listQuery.pageSize || DEFAULT_LIST_PAGE_SIZE));

  if (listQuery.keyword) {
    params.set('keyword', listQuery.keyword);
  }

  // BE sort 白名單為 snake；欄位 id（camel）不轉會整組靜默回預設排序
  (listQuery.sort || []).forEach(({ id, desc }) => {
    if (!id) {
      return;
    }
    params.append('sort', `${filterKeyToSnake(id)},${desc ? 'desc' : 'asc'}`);
  });

  Object.entries(listQuery.filters || {}).forEach(([key, value]) => {
    const snakeKey = filterKeyToSnake(key);

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) {
          params.append(snakeKey, item);
        }
      });
      return;
    }

    if (value) {
      params.set(snakeKey, value);
    }
  });

  return params;
}

export function buildListSearchParams(listQuery) {
  const params = new URLSearchParams();
  params.set('page', String(listQuery.page || DEFAULT_LIST_PAGE));
  params.set('page_size', String(listQuery.pageSize || DEFAULT_LIST_PAGE_SIZE));

  if (listQuery.keyword) {
    params.set('keyword', listQuery.keyword);
  }

  (listQuery.sort || []).forEach(({ id, desc }) => {
    params.append('sort', `${id},${desc ? 'desc' : 'asc'}`);
  });

  Object.entries(listQuery.filters || {}).forEach(([key, value]) => {
    const snakeKey = filterKeyToSnake(key);

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item) {
          params.append(snakeKey, item);
        }
      });
      return;
    }

    if (value) {
      params.set(snakeKey, value);
    }
  });

  return params;
}
